import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { InputText } from '../InputText';
import { TextArea } from '../TextArea';
import { SelectSingle } from '../SelectSingle';
import { RichTextEditor } from '../RichTextEditor';
import { X, Save, FileText, Loader2, Shield, Users, UserCheck } from 'lucide-react';
import { enqueueSnackbar } from 'notistack';
import { grupoGuiasService } from '../../services/grupoGuias';
import { api } from '../../services/api';
import { GrupoGuia, CreateGrupoGuiaDTO, UpdateGrupoGuiaDTO, GrupoCargo } from '../../types/grupos';

interface GrupoGuiaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  grupoId: string;
  guia?: GrupoGuia;
  categorias: string[];
}

export const GrupoGuiaModal: React.FC<GrupoGuiaModalProps> = ({
  isOpen,
  onClose,
  onSave,
  grupoId,
  guia,
  categorias
}) => {
  const [formData, setFormData] = useState({
    titulo: '',
    descricao: '',
    conteudo_html: '',
    categoria: '',
    tags: [] as string[],
    ordem: 0,
    tipo_acesso: 'publico',
    cargos_com_acesso: [] as string[]
  });
  const [tagInput, setTagInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [gruposCargos, setGruposCargos] = useState<GrupoCargo[]>([]);

  const isEditing = !!guia;

  const loadGruposCargos = async () => {
    try {
      const response = await api.get(`/grupos-cargos/grupo/${grupoId}`);
      setGruposCargos(response.data);
    } catch (error) {
      console.error('Erro ao carregar cargos do grupo:', error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (guia) {
        // Modo edição
        setFormData({
          titulo: guia.titulo,
          descricao: guia.descricao || '',
          conteudo_html: guia.conteudo_html,
          categoria: guia.categoria || '',
          tags: guia.tags ? JSON.parse(guia.tags) : [],
          ordem: guia.ordem,
          tipo_acesso: guia.tipo_acesso || 'publico',
          cargos_com_acesso: guia.cargosComAcesso?.map(cargo => cargo.id_cargo) || []
        });
      } else {
        // Modo criação
        setFormData({
          titulo: '',
          descricao: '',
          conteudo_html: '',
          categoria: '',
          tags: [],
          ordem: 0,
          tipo_acesso: 'publico',
          cargos_com_acesso: []
        });
      }
      setTagInput('');

      // Carregar cargos do grupo
      loadGruposCargos();
    }
  }, [isOpen, guia]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleTipoAcessoChange = (tipoAcesso: string) => {
    setFormData(prev => ({
      ...prev,
      tipo_acesso: tipoAcesso,
      cargos_com_acesso: tipoAcesso === 'restrito' ? prev.cargos_com_acesso : []
    }));
  };

  const handleCargoAcessoToggle = (cargoId: string) => {
    setFormData(prev => ({
      ...prev,
      cargos_com_acesso: prev.cargos_com_acesso.includes(cargoId)
        ? prev.cargos_com_acesso.filter(id => id !== cargoId)
        : [...prev.cargos_com_acesso, cargoId]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.titulo.trim()) {
      enqueueSnackbar('Título é obrigatório', { variant: 'error' });
      return;
    }

    if (!formData.conteudo_html.trim()) {
      enqueueSnackbar('Conteúdo é obrigatório', { variant: 'error' });
      return;
    }

    try {
      setIsSubmitting(true);

      if (isEditing && guia) {
        const updateData: UpdateGrupoGuiaDTO = {
          titulo: formData.titulo,
          descricao: formData.descricao,
          conteudo_html: formData.conteudo_html,
          categoria: formData.categoria || undefined,
          tags: formData.tags.length > 0 ? formData.tags : undefined,
          ordem: formData.ordem,
          tipo_acesso: formData.tipo_acesso,
          cargos_com_acesso: formData.tipo_acesso === 'restrito' ? formData.cargos_com_acesso : undefined
        };

        await grupoGuiasService.atualizar(guia.id, updateData);
        enqueueSnackbar('Guia atualizada com sucesso!', { variant: 'success' });
      } else {
        const createData: CreateGrupoGuiaDTO = {
          id_grupo: grupoId,
          titulo: formData.titulo,
          descricao: formData.descricao,
          conteudo_html: formData.conteudo_html,
          categoria: formData.categoria || undefined,
          tags: formData.tags.length > 0 ? formData.tags : undefined,
          ordem: formData.ordem,
          tipo_acesso: formData.tipo_acesso,
          cargos_com_acesso: formData.tipo_acesso === 'restrito' ? formData.cargos_com_acesso : undefined
        };

        await grupoGuiasService.criar(grupoId, createData);
        enqueueSnackbar('Guia criada com sucesso!', { variant: 'success' });
      }

      onSave();
      onClose();
    } catch (error: any) {
      console.error('Erro ao salvar guia:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Erro ao salvar guia',
        { variant: 'error' }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <Card className="w-full max-w-6xl max-h-[95vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              {isEditing ? 'Editar Guia' : 'Novo Guia'}
            </CardTitle>
            <CardDescription>
              {isEditing ? 'Edite as informações da guia' : 'Crie uma nova guia/apostila para o grupo'}
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-6 w-6 p-0"
            disabled={isSubmitting}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-6">
            {/* Título */}
            <div>
              <Label htmlFor="titulo">Título*</Label>
              <InputText
                id="titulo"
                value={formData.titulo}
                onChange={(e) => handleInputChange('titulo', e.target.value)}
                placeholder="Digite o título da guia..."
                required
                disabled={isSubmitting}
              />
            </div>

            {/* Descrição */}
            <div>
              <Label htmlFor="descricao">Descrição (opcional)</Label>
              <TextArea
                id="descricao"
                value={formData.descricao}
                onChange={(e) => handleInputChange('descricao', e.target.value)}
                placeholder="Breve descrição sobre o conteúdo da guia..."
                rows={2}
                disabled={isSubmitting}
              />
            </div>

            {/* Categoria */}
            <div>
              <Label htmlFor="categoria">Categoria (opcional)</Label>
              <div className="space-y-2">
                <SelectSingle
                  name="categoria"
                  title="Selecione uma categoria"
                  value={formData.categoria}
                  onChange={(e) => handleInputChange('categoria', e.target.value)}
                  disabled={isSubmitting}
                >
                  <option value="">Sem categoria</option>
                  {categorias.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </SelectSingle>
                <input
                  type="text"
                  placeholder="Ou digite uma nova categoria..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  value={formData.categoria}
                  onChange={(e) => handleInputChange('categoria', e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Ordem de exibição */}
            <div>
              <Label htmlFor="ordem">Ordem de exibição</Label>
              <InputText
                id="ordem"
                type="number"
                value={formData.ordem.toString()}
                onChange={(e) => handleInputChange('ordem', parseInt(e.target.value) || 0)}
                placeholder="0"
                disabled={isSubmitting}
              />
            </div>

            {/* Tags */}
            <div>
              <Label>Tags (opcional)</Label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Digite uma tag e pressione Enter..."
                  className="flex-1 px-3 py-2 border border-border bg-background text-foreground rounded-md text-sm"
                  disabled={isSubmitting}
                />
                <Button
                  type="button"
                  onClick={handleAddTag}
                  variant="outline"
                  size="sm"
                  disabled={isSubmitting}
                >
                  Adicionar
                </Button>
              </div>
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 text-xs rounded-md"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="hover:text-blue-600"
                        disabled={isSubmitting}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Controle de Acesso */}
            <div className="border border-border rounded-lg p-4 bg-muted/50">
              <div className="flex items-center gap-2 mb-4">
                <Shield className="h-5 w-5 text-blue-600" />
                <Label className="text-base font-semibold">Controle de Acesso</Label>
              </div>

              <div className="space-y-4">
                {/* Tipo de Acesso */}
                <div>
                  <Label className="text-sm font-medium">Quem pode acessar esta guia?</Label>
                  <div className="mt-2 space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="tipo_acesso"
                        value="publico"
                        checked={formData.tipo_acesso === 'publico'}
                        onChange={(e) => handleTipoAcessoChange(e.target.value)}
                        disabled={isSubmitting}
                        className="text-blue-600"
                      />
                      <Users className="h-4 w-4 text-green-600" />
                      <div>
                        <div className="font-medium">Todos os membros do grupo</div>
                        <div className="text-xs text-muted-foreground">Qualquer membro do grupo pode acessar</div>
                      </div>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="tipo_acesso"
                        value="restrito"
                        checked={formData.tipo_acesso === 'restrito'}
                        onChange={(e) => handleTipoAcessoChange(e.target.value)}
                        disabled={isSubmitting}
                        className="text-blue-600"
                      />
                      <UserCheck className="h-4 w-4 text-orange-600" />
                      <div>
                        <div className="font-medium">Apenas cargos específicos</div>
                        <div className="text-xs text-muted-foreground">Selecione os cargos que podem acessar</div>
                      </div>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="tipo_acesso"
                        value="admin"
                        checked={formData.tipo_acesso === 'admin'}
                        onChange={(e) => handleTipoAcessoChange(e.target.value)}
                        disabled={isSubmitting}
                        className="text-blue-600"
                      />
                      <Shield className="h-4 w-4 text-red-600" />
                      <div>
                        <div className="font-medium">Apenas administradores</div>
                        <div className="text-xs text-muted-foreground">Somente líderes e administradores</div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Seleção de Cargos - só aparece se tipo_acesso for 'restrito' */}
                {formData.tipo_acesso === 'restrito' && (
                  <div>
                    <Label className="text-sm font-medium">Selecione os cargos com acesso:</Label>
                    <div className="mt-2 space-y-2 max-h-32 overflow-y-auto border border-border rounded p-2 bg-background">
                      {gruposCargos.map(cargo => (
                        <label key={cargo.id} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.cargos_com_acesso.includes(cargo.id)}
                            onChange={() => handleCargoAcessoToggle(cargo.id)}
                            disabled={isSubmitting}
                            className="text-blue-600"
                          />
                          <div>
                            <div className="font-medium text-sm">{cargo.cargo}</div>
                            {cargo.descricao && (
                              <div className="text-xs text-muted-foreground">{cargo.descricao}</div>
                            )}
                          </div>
                        </label>
                      ))}
                      {gruposCargos.length === 0 && (
                        <div className="text-sm text-muted-foreground text-center py-2">
                          Nenhum cargo encontrado para este grupo
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Conteúdo HTML */}
            <div>
              <Label>Conteúdo*</Label>
              <RichTextEditor
                value={formData.conteudo_html}
                onChange={(content) => handleInputChange('conteudo_html', content)}
                placeholder="Digite o conteúdo da guia/apostila..."
                height="400px"
                enableImages
              />
            </div>
          </CardContent>

          <div className="flex gap-3 p-6 pt-0">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isEditing ? 'Atualizar' : 'Criar Guia'}
                </>
              )}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};
